import { useEffect, useRef } from 'react';

export const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
export const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

/** One scrollable, snap-to-center wheel column (used for both hour and minute). */
function Wheel({ values, index, onChange, disabled }) {
  const ref = useRef(null);
  const itemHeightRef = useRef(0);
  const debounceRef = useRef(null);

  const updateCurrentClass = () => {
    const el = ref.current;
    if (!el || !itemHeightRef.current) return;
    const idx = Math.round(el.scrollTop / itemHeightRef.current);
    [...el.children].forEach((child, i) => child.classList.toggle('current', i === idx));
  };

  // Initial mount: measure item height and scroll to the given index.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const firstItem = el.querySelector('.wheel-item');
    itemHeightRef.current = firstItem ? firstItem.offsetHeight : 0;
    el.scrollTop = index * itemHeightRef.current;
    updateCurrentClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the wheel in sync if the index changes from outside
  // (e.g. a template selection sets the time programmatically).
  useEffect(() => {
    const el = ref.current;
    if (!el || !itemHeightRef.current) return;
    const target = index * itemHeightRef.current;
    if (Math.abs(el.scrollTop - target) > 1) {
      el.scrollTo({ top: target, behavior: 'smooth' });
    }
    updateCurrentClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const handleScroll = () => {
    updateCurrentClass();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const el = ref.current;
      if (!el || !itemHeightRef.current) return;
      const idx = Math.round(el.scrollTop / itemHeightRef.current);
      if (values[idx] !== undefined && idx !== index) onChange(idx);
    }, 90);
  };

  return (
    <div
      className="wheel"
      ref={ref}
      onScroll={disabled ? undefined : handleScroll}
      style={disabled ? { pointerEvents: 'none' } : undefined}
    >
      {values.map((v) => (
        <div className="wheel-item" key={v}>
          {v}
        </div>
      ))}
    </div>
  );
}

/**
 * One "開始" / "終了" column: label + hour:minute wheel pair.
 * hourIndex/minuteIndex are indices into HOURS/MINUTES; the parent
 * (EventFormSheet) owns the actual state so it can read out "HH:MM"
 * strings when building the event payload.
 */
export default function TimeWheelPicker({ label, hourIndex, minuteIndex, onChangeHour, onChangeMinute, disabled }) {
  return (
    <div className="time-col">
      <div className="time-col-label">{label}</div>
      <div className="time-wheel-wrap">
        <div className="wheel-highlight" />
        <Wheel values={HOURS} index={hourIndex} onChange={onChangeHour} disabled={disabled} />
        <div className="wheel-colon">:</div>
        <Wheel values={MINUTES} index={minuteIndex} onChange={onChangeMinute} disabled={disabled} />
      </div>
    </div>
  );
}
